/* eslint-disable functional/no-expression-statement,@typescript-eslint/ban-ts-comment */
import * as D3Brush from "d3-brush";
import * as D3Scale from "d3-scale";
import * as D3Selection from "d3-selection";
import React from "react";
import styled from "styled-components";

import * as Animation from "~/Animation";
import * as Color from "~/Color";
import * as Driver from "~/Driver";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Time } from "~/Time";
import { Timeline } from "~/Timeline";
import * as EventHandler from "~/Timeline/Brusher/Event";

import * as Axis from "./Axis";
import * as Milestones from "./Milestone";
import * as PairingEvents from "./PairingEvents";
import * as RevisionEvents from "./RevisionEvents";
import * as StatusEvents from "./StatusEvents";
import * as ViolationEvents from "./ViolationEvents";

const Container = styled.div`
  display: flex;
  flex: 1;
  background-color: ${Color.white()};
`;

export const Component: Component = () => (
  <Layout.Section.Container.Component style={{ height: "5.5em" }}>
    <Animation.FadeIn.Component>
      <Container className="log-chart-time-selector" />
    </Animation.FadeIn.Component>
  </Layout.Section.Container.Component>
);

const brushUpdate = (chartProps: Log.Chart.ChartGeneratorProps) => {
  StatusEvents.update(chartProps);
  Milestones.update(chartProps);
  Axis.Xaxis.update(chartProps);
  RevisionEvents.update(chartProps);
  ViolationEvents.update(chartProps);
  PairingEvents.update(chartProps);
};

export namespace SelectorChart {
  const className = `log-chart-time-selector` as const;
  const height = 100;
  const topMargin = 10;
  const leftMargin = 80;
  const rightMargin = 80;
  const bottomMargin = 40;

  type ChartProps = {
    readonly chartProps: Log.Chart.ChartGeneratorProps;
    readonly data: readonly Log.Chart.LogChartData[];
    readonly width: number;
    readonly defaultZone: Time.Zone;
    readonly driverZone: Time.Zone;
    readonly saveBrushSelection: (
      selection: Timeline.Brush.BrushSelection
    ) => void;
  };

  export const generateSelectorChart = ({
    chartProps,
    data,
    width,
    defaultZone,
    saveBrushSelection,
  }: ChartProps): Timeline.Brusher.BrushHandlers => {
    const brushHeight: number = height - topMargin - bottomMargin;
    const brushWidth: number = width - leftMargin - rightMargin;

    type Selection = {
      readonly selection: readonly [number, number];
    };

    const onBrush = (
      selection: Selection,
      chartProps: Log.Chart.ChartGeneratorProps,
      xScale: D3Scale.ScaleTime<number, number, never>
    ) => {
      if (selection.selection) {
        chartProps.xScale.domain(
          selection
            ? [
                xScale.invert(selection.selection[0]),
                xScale.invert(selection.selection[1]),
              ]
            : xScale.domain()
        );

        brushUpdate(chartProps);
        saveBrushSelection(selection.selection);
      }
    };

    // Remove the events that make up the end of each step within the stepped status events so that we
    // are only working with one point per event.
    const brushData = data.filter((_d, index) => index % 2 === 0);

    const xScale = chartProps.xScale.copy();

    // eslint-disable-next-line functional/no-expression-statement
    D3Selection.selectAll(`.${className}-svg`).remove();

    const svg = D3Selection.select(`.${className}`)
      .append("svg")
      .attr("class", `${className}-svg`)
      .attr("width", width)
      .attr("height", height)
      .attr("style", `background-color: ${Color.white()}`)
      .append("g")
      .attr("transform", `translate(${leftMargin},${topMargin})`);

    // eslint-disable-next-line functional/no-expression-statement
    Axis.Xaxis.generate({
      svg,
      chartHeight: brushHeight,
      xScale,
    } as Log.Chart.ChartGeneratorProps);

    const bg = svg.append("g").attr("class", `${className}-bg brush`);

    const brush = D3Brush.brushX<Log.Chart.LogChartData>()
      .handleSize(15)
      .extent([
        [0, 0],
        [brushWidth, brushHeight],
      ])
      .on("brush", (selection) => onBrush(selection, chartProps, xScale));

    svg.append("g").attr("clip-path", "url(#clip)");

    svg
      .selectAll("focus-status-line")
      .data(brushData)
      .enter()
      .append("line")
      .attr("class", "focus-status-line")
      .attr("x1", (l: Log.Chart.LogChartData) =>
        StatusEvents.getXScaledValue(l.start, xScale, defaultZone)
      )
      .attr("x2", (l: Log.Chart.LogChartData) =>
        StatusEvents.getXScaledValue(l.stop, xScale, defaultZone)
      )
      .attr("y1", brushHeight / 2)
      .attr("y2", brushHeight / 2)
      .attr("fill", "none")
      .attr("shape-rendering", "crispEdges")
      .attr("stroke", (l: Log.Chart.LogChartData) =>
        Driver.Status.toColor(l.status)
      )
      .attr("stroke-width", "8px");

    // @ts-ignore
    bg.call(brush);

    const startOfDayForZone = BrushEventHandlers.brushStartOfDayForZone(
      brushWidth,
      defaultZone
    );

    // Initially select the driver's day on the time slider to be consistent with the default
    // display of the timeline.
    const defaultSelection = BrushEventHandlers.defaultToFullDay(brushWidth)
      ? BrushEventHandlers.defaultSelection(brushWidth, startOfDayForZone, 25)
      : BrushEventHandlers.defaultSelection(brushWidth, startOfDayForZone, 13);
    // @ts-ignore
    BrushEventHandlers.moveBrush(bg, brush, defaultSelection);
    onBrush({ selection: defaultSelection }, chartProps, xScale);

    return BrushEventHandlers.generate(
      brushWidth,
      // @ts-ignore
      bg,
      brush,
      startOfDayForZone
    );
  };

  export namespace BrushEventHandlers {
    export const oneHour = (maximumDomainValue: number): number =>
      maximumDomainValue / 48;

    export const defaultToFullDay = (maximumDomainValue: number): boolean =>
      maximumDomainValue > 1700;

    export const defaultSelection = (
      maximumDomainValue: number,
      startOfDayOffset: number,
      hours: number
    ): Timeline.Brush.BrushSelection => {
      const minValue = startOfDayOffset;
      const maxValue =
        startOfDayOffset + oneHour(maximumDomainValue) * (hours + 1);
      return [
        minValue < 0 ? 0 : minValue,
        maxValue > maximumDomainValue ? maximumDomainValue : maxValue,
      ];
    };

    export const brushStartOfDayForZone = (
      brushWidth: number,
      defaultZone: Time.Zone
    ): number => {
      const zoneDelta = Time.Zone.toOffset(defaultZone) - Time.Zone.toOffset();
      return EventHandler.oneHour(brushWidth) * (11 - zoneDelta / 60);
    };

    const contractLeft = (
      selection: Timeline.Brush.BrushSelection,
      brushWidth: number
    ): Timeline.Brush.BrushSelection => [
      Math.min(selection[1], selection[0] + oneHour(brushWidth)),
      selection[1],
    ];

    const contractRight = (
      selection: Timeline.Brush.BrushSelection,
      brushWidth: number
    ): Timeline.Brush.BrushSelection => [
      selection[0],
      Math.max(selection[0], selection[1] - oneHour(brushWidth)),
    ];

    const expandLeft = (
      selection: Timeline.Brush.BrushSelection,
      brushWidth: number
    ): Timeline.Brush.BrushSelection => [
      Math.max(0, selection[0] - oneHour(brushWidth)),
      selection[1],
    ];

    const expandRight = (
      selection: Timeline.Brush.BrushSelection,
      brushWidth: number
    ): Timeline.Brush.BrushSelection => [
      selection[0],
      Math.min(brushWidth, selection[1] + oneHour(brushWidth)),
    ];

    const brushSelections = (
      bg: D3Selection.Selection<SVGGElement, unknown, HTMLElement, never>
    ): Timeline.Brush.BrushSelection | undefined => {
      if (bg) {
        const node = bg.node() ?? undefined;
        if (node) {
          return D3Brush.brushSelection(node) as Timeline.Brush.BrushSelection;
        }
      }
      return undefined;
    };

    export const moveBrush = (
      bg: D3Selection.Selection<SVGGElement, unknown, HTMLElement, never>,
      brush: D3Brush.BrushBehavior<Log.Chart.LogChartData>,
      selection: Timeline.Brush.BrushSelection | null
    ) => {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/unbound-method
      bg.call(brush.move, selection);
    };

    export const generate = (
      brushWidth: number,
      bg: D3Selection.Selection<SVGGElement, unknown, HTMLElement, never>,
      brush: D3Brush.BrushBehavior<Log.Chart.LogChartData>,
      startOfDay: number
    ): Timeline.Brusher.BrushHandlers => {
      const minTime: number = oneHour(brushWidth);
      return {
        onSetBrush: (selection) => moveBrush(bg, brush, selection),
        onLeft: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection && brushSelection[0] > 0) {
            const moveDelta: number = Math.min(
              Math.max(brushSelection[0] - minTime, 0),
              minTime
            );
            moveBrush(bg, brush, [
              Math.max(brushSelection[0] - moveDelta, 0),
              Math.max(brushSelection[1] - moveDelta, moveDelta),
            ]);
          }
        },
        onLeftMax: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection && brushSelection[0] > 0) {
            moveBrush(bg, brush, [0, brushSelection[1] - brushSelection[0]]);
          }
        },
        onRight: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection && brushSelection[1] < brushWidth) {
            const moveDelta: number = Math.min(
              Math.max(brushWidth - brushSelection[1], 0),
              minTime
            );
            moveBrush(bg, brush, [
              Math.min(brushSelection[0] + moveDelta, brushWidth - 1),
              Math.min(brushSelection[1] + moveDelta, brushWidth),
            ]);
          }
        },
        onRightMax: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection) {
            moveBrush(bg, brush, [
              brushWidth - (brushSelection[1] - brushSelection[0]),
              brushWidth,
            ]);
          }
        },
        onZoomIn: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection) {
            const newSelection = contractRight(
              contractLeft(brushSelection, brushWidth),
              brushWidth
            );
            if (
              newSelection[0] !== brushSelection[0] ||
              newSelection[1] !== brushSelection[1]
            ) {
              moveBrush(bg, brush, newSelection);
            }
          } else {
            moveBrush(bg, brush, [minTime, brushWidth - minTime]);
          }
        },
        onZoomOut: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection) {
            const newSelection = expandRight(
              expandLeft(brushSelection, brushWidth),
              brushWidth
            );
            moveBrush(bg, brush, newSelection);
            if (
              newSelection[0] !== brushSelection[0] ||
              newSelection[1] !== brushSelection[1]
            ) {
              moveBrush(bg, brush, newSelection);
            }
          }
        },
        onZoomReset: () => {
          const brushSelection = brushSelections(bg);
          if (brushSelection) {
            const selection = defaultToFullDay(brushWidth)
              ? defaultSelection(brushWidth, startOfDay, 25)
              : defaultSelection(brushWidth, startOfDay, 13);

            moveBrush(bg, brush, selection);
          }
        },
      };
    };

    export const defaultBrushHandlers: Timeline.Brusher.BrushHandlers = {
      onSetBrush: () => {},
      onLeft: () => {},
      onLeftMax: () => {},
      onRight: () => {},
      onRightMax: () => {},
      onZoomIn: () => {},
      onZoomOut: () => {},
      onZoomReset: () => {},
    };
  }
}
